import React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { BadgeCheck, Building2, MapPin, Star, MessageCircle } from 'lucide-react';
import { getTypeLabel } from '@/constants/entityTypes';
import { getEntityImageUrl } from '@/lib/entityMedia';

const EntityHeader = ({ entity, onConnect }) => {
  const typeLabel = getTypeLabel(entity.entity_type);
  // Extract — visual-edits Babel plugin dislikes repeated prop chains inside && guards.
  const businessGroup = entity.business_group;

  return (
    <div className="bg-card rounded-3xl border border-border/50 p-4 sm:p-6 md:p-8 mb-6 sm:mb-8 shadow-xl">
      <div className="flex flex-col md:flex-row md:items-start gap-6">
        {/* Avatar for guides */}
        {entity.entity_type === 'outfitters_guides' && (
          <Avatar className="h-24 w-24 border-4 border-background shadow-lg">
            <AvatarImage src={getEntityImageUrl(entity, { width: 400 })} alt={entity.name} />
            <AvatarFallback className="text-2xl bg-primary text-primary-foreground">
              {entity.name?.[0]}
            </AvatarFallback>
          </Avatar>
        )}

        <div className="flex-1">
          <div className="flex flex-wrap items-center gap-3 mb-2">
            <h1 className="text-2xl md:text-3xl font-bold">{entity.name}</h1>
            {entity.verified && (
              <Badge className="gap-1 bg-primary/10 text-primary border-0">
                <BadgeCheck className="h-3 w-3" />
                Verified
              </Badge>
            )}
          </div>

          <p className="text-muted-foreground mb-4">{typeLabel}</p>

          {businessGroup ? (
            <div
              className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 mb-4 text-sm text-primary"
              data-testid="entity-business-group"
            >
              <Building2 className="h-3.5 w-3.5" />
              <span>Part of <span className="font-semibold">{businessGroup}</span></span>
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-4 text-sm">
            {entity.location && (
              <span className="flex items-center gap-1 text-muted-foreground">
                <MapPin className="h-4 w-4" />
                {entity.location}
              </span>
            )}
            {entity.rating > 0 && (
              <span className="flex items-center gap-1">
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                <span className="font-medium">{entity.rating.toFixed(1)}</span>
                <span className="text-muted-foreground">({entity.review_count} reviews)</span>
              </span>
            )}
          </div>
        </div>

        <Button size="lg" className="rounded-full" onClick={onConnect} data-testid="entity-connect-btn">
          <MessageCircle className="mr-2 h-5 w-5" />
          Connect
        </Button>
      </div>
    </div>
  );
};

export default EntityHeader;
